import {
	IconArrowRight,
	IconCode,
	IconLayoutGrid,
	IconMarkdown,
	IconPalette,
	IconPhoto,
	type IconProps,
	IconVector,
} from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ForwardRefExoticComponent, RefAttributes } from "react";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { buttonVariants } from "@/components/ui/button";
import {
	Card,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	MARKETING_TOOL_DEFINITIONS,
	type MarketingToolDefinition,
	partitionMarketingToolsByType,
} from "@/data/marketing-tools";
import { cn } from "@/lib/utils";
import {
	getStaggeredAnimationDelayStyle,
	MARKETING_GRID_CARD_CLASSES,
	MARKETING_SECTION_ANIM_BASE,
} from "@/utils/animation";
import { seo } from "@/utils/seo";

export type ToolIcon = ForwardRefExoticComponent<
	IconProps & RefAttributes<SVGSVGElement>
>;

export type ToolConfig = {
	title: string;
	description: string;
	href: string;
	icon: ToolIcon;
	beta?: boolean;
};

/** Icons keyed by tool title — placeholder `href`s are not unique in the template. */
const TOOL_ICONS: Record<string, ToolIcon> = {
	"Color Palette Generator": IconPalette,
	"Image Optimizer": IconPhoto,
	"CSS Flexbox Generator": IconLayoutGrid,
	"JSON Formatter": IconCode,
	"SVG Editor": IconVector,
	"Markdown Preview": IconMarkdown,
};

function buildToolsGridConfig(
	definitions: readonly MarketingToolDefinition[],
): ToolConfig[] {
	return definitions.map((def) => {
		const icon = TOOL_ICONS[def.title];
		if (!icon) {
			throw new Error(`Missing TOOL_ICONS entry for ${def.title}`);
		}

		return {
			title: def.title,
			description: def.description,
			href: def.href,
			icon,
			beta: def.beta,
		};
	});
}

const { single: singleDefinitions, bulk: bulkDefinitions } =
	partitionMarketingToolsByType(MARKETING_TOOL_DEFINITIONS);

export const singleTools: ToolConfig[] =
	buildToolsGridConfig(singleDefinitions);
export const bulkTools: ToolConfig[] = buildToolsGridConfig(bulkDefinitions);
export const tools: ToolConfig[] = [...singleTools, ...bulkTools];

function ToolGridCard({ tool, index }: { tool: ToolConfig; index: number }) {
	const Icon = tool.icon;
	return (
		<Card
			className={cn(
				MARKETING_GRID_CARD_CLASSES,
				"hover:border-primary/40 group",
			)}
			style={getStaggeredAnimationDelayStyle(index, 0.4)}
		>
			{tool.beta ? (
				<span className="absolute end-3 top-3 rounded-full border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">
					Beta
				</span>
			) : null}
			<Link
				to={tool.href}
				className="flex flex-1 flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
			>
				<CardHeader className="flex flex-col gap-4 pb-4">
					<div className="flex size-11 items-center justify-center rounded-lg border border-border/60 bg-muted/40">
						<Icon
							className="size-5 text-primary transition-transform duration-200 group-hover:scale-105"
							aria-hidden
						/>
					</div>
					<CardTitle className="text-lg font-semibold tracking-tight text-foreground transition-colors group-hover:text-primary">
						{tool.title}
					</CardTitle>
					<CardDescription className="text-base text-pretty text-muted-foreground">
						{tool.description}
					</CardDescription>
				</CardHeader>
				<CardFooter className="mt-auto border-t border-border/60 pt-4">
					<div className="flex w-full items-center justify-between text-sm font-medium text-primary">
						<span>Open tool</span>
						<IconArrowRight
							className="size-4 transition-transform duration-200 group-hover:translate-x-0.5"
							aria-hidden
						/>
					</div>
				</CardFooter>
			</Link>
		</Card>
	);
}

function ToolsSection({
	id,
	heading,
	description,
	tools: sectionTools,
	staggerOffset = 0,
}: {
	id: string;
	heading: string;
	description: string;
	tools: ToolConfig[];
	staggerOffset?: number;
}) {
	return (
		<section id={id} className="scroll-mt-28">
			<div className="mb-8 flex flex-col gap-2 sm:mb-10">
				<h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
					{heading}
				</h2>
				<p className="max-w-2xl text-base text-pretty text-muted-foreground">
					{description}
				</p>
			</div>
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
				{sectionTools.map((tool, index) => (
					<ToolGridCard
						key={tool.title}
						tool={tool}
						index={staggerOffset + index}
					/>
				))}
			</div>
		</section>
	);
}

export const Route = createFileRoute("/_marketing/tools/")({
	component: RouteComponent,
	head: () =>
		seo({
			title: "Tools",
			description: "Tools to help you get started.",
			keywords:
				"tools, tools for developers, tools for designers, tools for marketers, tools for entrepreneurs",
		}),
});

export function RouteComponent() {
	return (
		<MarketingShell>
			<section className="pt-20 pb-16 sm:pt-28 sm:pb-24">
				<MarketingContainer>
					<MarketingHeader
						title="Toolkit"
						heading="Utilities for builders and operators"
						description="Lightweight helpers you can drop into a sprint—no installs, no accounts required. More are on the roadmap."
						actions={
							<div className="flex flex-wrap gap-2">
								<a
									href="#single-tools"
									className={cn(
										buttonVariants({ variant: "outline", size: "sm" }),
										"rounded-full",
									)}
								>
									Single-target tools
								</a>
								<a
									href="#bulk-tools"
									className={cn(
										buttonVariants({ variant: "outline", size: "sm" }),
										"rounded-full",
									)}
								>
									Bulk tools
								</a>
							</div>
						}
					/>

					<div
						className={cn(
							MARKETING_SECTION_ANIM_BASE,
							"delay-200 flex flex-col gap-16 sm:gap-20",
						)}
					>
						<ToolsSection
							id="single-tools"
							heading="Interactive utilities"
							description="One-off helpers for design, markup, and debugging—pick a tool and go."
							tools={singleTools}
						/>

						<ToolsSection
							id="bulk-tools"
							heading="Batch utilities"
							description="Run the same operation across many inputs at once—useful when you are processing lists or assets in one pass."
							tools={bulkTools}
							staggerOffset={singleTools.length}
						/>
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
